/**
 * EAS ROI Calculation Service
 *
 * Calculates operational ROI metrics for Equipment Assessment Sheet data.
 * Uses corpus-validated degradation model and equipment-specific multipliers.
 *
 * DATA FLOW:
 * 1. parseEASFile() → equipment array
 * 2. calculateEASROI() → portfolio + type + individual metrics
 * 3. Display in UI + export to report
 *
 * KEY ASSUMPTIONS:
 * - Treatment cost: $125 per ton
 * - Equipment multipliers: Chiller 1.3, RTU 1.4, CRAC 1.5, Split 1.4
 * - Corpus degradation model (83% more accurate than ASHRAE)
 * - Environmental: 0.42 kg CO2/kWh, 4.6 tonnes/car/year
 * - Age modifiers apply to improvement rates
 *
 * 6-COMPONENT CORPUS-VALIDATED VALUE MODEL (v3.1):
 * Total Value = Energy Savings (116 PE-certified studies)
 *             + Avoided Future Degradation (ASHRAE 1998 Handbook Ch 2.9)
 *             + Avoided Peak Demand Charges (Industry kW/ton metrics)
 *             + Avoided Extended Runtime (50% capacity-to-runtime factor)
 *             + Life Extension Value (20% per roi_calculator_logic.json)
 *             + Mission-Critical Protection (MTBF improvement × facility downtime cost)
 *
 * REMOVED (no quantified corpus support):
 * - Avoided Maintenance (30%) - qualitative only: "fewer repairs"
 * - Avoided Refrigerant (40%) - "may reduce" language, no data
 * - Avoided Compressor (50%) - lab wear data ≠ field failure rates
 */

import {
  calculateAvoidedDegradationCosts,
  calculateAvoidedDemandCharges,
  calculateAvoidedExtendedRuntimeCosts,
  calculateLifeExtensionValue,
  calculateMissionCriticalProtection,
} from './degradationCurve';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Treatment cost per ton of cooling capacity
 */
export const TREATMENT_COST_PER_TON = 125;

/**
 * Equipment-specific kW per ton multipliers
 * Based on typical baseline efficiency for each equipment type
 */
export const EQUIPMENT_MULTIPLIERS = {
  'Chiller': 1.3,
  'RTU': 1.4,
  'CRAC': 1.5,
  'Split': 1.4
};

/**
 * Environmental conversion factors
 */
export const ENVIRONMENTAL_FACTORS = {
  KWH_TO_CO2_KG: 0.42,           // kg CO2 per kWh (US grid average)
  CO2_TONNES_PER_CAR_YEAR: 4.6   // Annual CO2 from average passenger vehicle
};

// ============================================================================
// DEGRADATION MODEL - Corpus Validated (83% more accurate than ASHRAE)
// ============================================================================

/**
 * Default electricity price escalation rate (3% per year)
 * Based on historical US average electricity price increases
 */
export const DEFAULT_ESCALATION_RATE = 0.03;

/**
 * Calculate efficiency degradation based on equipment age
 * Uses Powertron corpus-validated model
 *
 * @param {number} ageYears - Equipment age in years
 * @returns {number} Degradation percentage (0.10 to 0.26)
 */
function corpusDegradation(ageYears) {
  if (ageYears <= 0) return 0.10;      // 10% minimum
  if (ageYears <= 5) return 0.15 + (ageYears * 0.012);       // ~15-21%
  if (ageYears <= 15) return 0.21 + ((ageYears - 5) * 0.005); // ~21-26%
  return Math.max(0.20, 0.26 - ((ageYears - 15) * 0.003));   // After 15 years, plateau
}

/**
 * Get age modifier for calculation adjustments
 * Newer equipment (<5yr) sees less improvement due to better baseline
 * Older equipment (10-15yr) sees more improvement due to greater degradation
 *
 * @param {number} age - Equipment age in years
 * @returns {number} Age modifier (0.85 to 1.05)
 */
function getAgeModifier(age) {
  if (age < 5) return 0.85;   // Newer equipment, less degradation
  if (age < 10) return 1.0;   // Mid-life equipment, standard
  if (age < 15) return 1.05;  // Older equipment, more degradation
  return 1.0;                 // Very old equipment, plateau
}

/**
 * Get run hours modifier for degradation adjustment
 * Based on 2017 IPP Calculator: ~0.6% additional degradation per 1000 run hours above 2000 baseline
 * Equipment running more hours accumulates more thermal degradation
 *
 * @param {number} operatingHours - Annual operating hours
 * @returns {number} Run hours modifier (0.9 to 1.3)
 */
function getRunHoursModifier(operatingHours) {
  const baseline = 2000;  // Standard baseline hours
  const ratePerThousandHours = 0.025;  // ~2.5% adjustment per 1000 hours deviation

  // Calculate deviation from baseline
  const hoursDeviation = operatingHours - baseline;
  const thousandsDeviation = hoursDeviation / 1000;

  // Apply modifier (capped at reasonable bounds)
  const modifier = 1 + (thousandsDeviation * ratePerThousandHours);
  return Math.max(0.9, Math.min(1.3, modifier));  // Cap between 0.9x and 1.3x
}

/**
 * Calculate improvement rate for equipment
 * Combines corpus degradation model with equipment type factors and run hours
 *
 * @param {string} equipmentType - Equipment type (Chiller, RTU, CRAC, Split)
 * @param {number} age - Equipment age in years
 * @param {number} operatingHours - Annual operating hours (optional, default 2000)
 * @returns {number} Improvement rate as decimal (e.g., 0.21 = 21%)
 */
function getImprovementRate(equipmentType, age, operatingHours = 2000) {
  const baseDegradation = corpusDegradation(age);
  const ageModifier = getAgeModifier(age);
  const runHoursModifier = getRunHoursModifier(operatingHours);

  // Equipment-specific adjustments
  let equipmentFactor = 1.0;
  switch (equipmentType) {
    case 'Chiller':
      equipmentFactor = 1.0;   // Base rate
      break;
    case 'RTU':
      equipmentFactor = 1.06;  // RTUs typically see 6% more improvement
      break;
    case 'CRAC':
      equipmentFactor = 1.24;  // CRACs see 24% more improvement
      break;
    case 'Split':
      equipmentFactor = 1.15;  // Split systems see 15% more improvement
      break;
    default:
      equipmentFactor = 1.0;
  }

  return baseDegradation * ageModifier * equipmentFactor * runHoursModifier;
}

// ============================================================================
// WASTED ENERGY CALCULATION - Shows historical energy loss before treatment
// ============================================================================

/**
 * Calculate cumulative energy wasted before treatment
 * Shows the cost of delayed action - energy lost over equipment lifetime
 *
 * @param {string} equipmentType - Equipment type
 * @param {number} tonnage - Equipment tonnage
 * @param {number} currentAge - Current equipment age in years
 * @param {number} operatingHours - Annual operating hours
 * @param {number} electricityRate - Electricity rate $/kWh
 * @returns {Object} Wasted energy metrics
 */
export function calculateWastedEnergy(equipmentType, tonnage, currentAge, operatingHours, electricityRate) {
  const multiplier = EQUIPMENT_MULTIPLIERS[equipmentType] || 1.4;

  let totalWastedKwh = 0;
  const yearlyWaste = [];

  // Calculate waste for each year from 1 to current age
  for (let year = 1; year <= currentAge; year++) {
    const yearDegradation = corpusDegradation(year);
    const runHoursModifier = getRunHoursModifier(operatingHours);
    const yearlyKwhLost = tonnage * multiplier * operatingHours * yearDegradation * runHoursModifier;

    totalWastedKwh += yearlyKwhLost;
    yearlyWaste.push({
      year,
      degradation: yearDegradation,
      kwhLost: yearlyKwhLost,
      dollarsLost: yearlyKwhLost * electricityRate
    });
  }

  const totalWastedDollars = totalWastedKwh * electricityRate;

  return {
    totalWastedKwh,
    totalWastedDollars,
    yearsWasted: currentAge,
    averageYearlyWasteKwh: currentAge > 0 ? totalWastedKwh / currentAge : 0,
    averageYearlyWasteDollars: currentAge > 0 ? totalWastedDollars / currentAge : 0,
    yearlyBreakdown: yearlyWaste,
    message: `Over ${currentAge} years, this equipment has wasted approximately ${Math.round(totalWastedKwh).toLocaleString()} kWh ($${Math.round(totalWastedDollars).toLocaleString()}) due to thermal degradation.`
  };
}

// ============================================================================
// PRICE ESCALATION PROJECTIONS - Future savings with electricity rate increases
// ============================================================================

/**
 * Calculate year-by-year projections with electricity price escalation
 * Real-world electricity prices typically increase 2-4% annually
 *
 * @param {number} annualValue - Base annual value/savings
 * @param {number} years - Number of years to project
 * @param {number} escalationRate - Annual price increase rate (default 3%)
 * @param {number} treatmentCost - Treatment cost for ROI calculation
 * @returns {Object} Detailed yearly projections
 */
export function calculateProjectionsWithEscalation(annualValue, years, escalationRate = DEFAULT_ESCALATION_RATE, treatmentCost = 0) {
  const yearlyProjections = [];
  let cumulative = 0;
  let cumulativeNoEscalation = 0;

  for (let year = 1; year <= years; year++) {
    // Calculate escalated value: Year N = Year 1 × (1 + rate)^(N-1)
    const escalationMultiplier = Math.pow(1 + escalationRate, year - 1);
    const escalatedValue = annualValue * escalationMultiplier;
    const flatValue = annualValue;  // No escalation comparison

    cumulative += escalatedValue;
    cumulativeNoEscalation += flatValue;

    // Calculate running ROI
    const netPosition = cumulative - treatmentCost;
    const roi = treatmentCost > 0 ? ((cumulative - treatmentCost) / treatmentCost) * 100 : 0;

    yearlyProjections.push({
      year,
      escalationMultiplier,
      annualValue: escalatedValue,
      annualValueFlat: flatValue,
      cumulative,
      cumulativeFlat: cumulativeNoEscalation,
      netPosition,
      roi,
      escalationBenefit: cumulative - cumulativeNoEscalation
    });
  }

  // Summary statistics
  const finalYear = yearlyProjections[yearlyProjections.length - 1];
  const escalationBenefit = finalYear.cumulative - finalYear.cumulativeFlat;
  const escalationBenefitPct = finalYear.cumulativeFlat > 0
    ? (escalationBenefit / finalYear.cumulativeFlat) * 100
    : 0;

  return {
    yearlyProjections,
    summary: {
      totalWithEscalation: finalYear.cumulative,
      totalWithoutEscalation: finalYear.cumulativeFlat,
      escalationBenefit,
      escalationBenefitPct,
      finalROI: finalYear.roi,
      paybackYear: yearlyProjections.find(y => y.netPosition >= 0)?.year || null
    },
    parameters: {
      baseAnnualValue: annualValue,
      escalationRate,
      years,
      treatmentCost
    }
  };
}

/**
 * Generate before/after treatment data for visualization
 * Shows wasted energy before treatment and projected savings after
 *
 * @param {Object} params - Parameters for calculation
 * @returns {Object} Data for before/after chart
 */
export function generateBeforeAfterData({
  equipmentType,
  tonnage,
  currentAge,
  operatingHours,
  electricityRate,
  projectionYears = 13,  // Project to typical 25-year equipment life
  escalationRate = DEFAULT_ESCALATION_RATE
}) {
  const multiplier = EQUIPMENT_MULTIPLIERS[equipmentType] || 1.4;
  const treatmentYear = currentAge;
  const endYear = Math.min(currentAge + projectionYears, 25);

  const beforeTreatment = [];  // Wasted kWh per year
  const afterTreatment = [];   // Saved kWh per year
  const labels = [];

  // Before treatment: years 1 to current age (wasted energy)
  for (let year = 1; year <= currentAge; year++) {
    const degradation = corpusDegradation(year);
    const runHoursModifier = getRunHoursModifier(operatingHours);
    const wastedKwh = tonnage * multiplier * operatingHours * degradation * runHoursModifier;

    labels.push(`Year ${year}`);
    beforeTreatment.push(Math.round(wastedKwh));
    afterTreatment.push(0);  // No savings before treatment
  }

  // After treatment: current age to end year (saved energy)
  const improvementRate = getImprovementRate(equipmentType, currentAge, operatingHours);
  const baseKwhSaved = tonnage * multiplier * operatingHours * improvementRate;

  for (let year = currentAge + 1; year <= endYear; year++) {
    const yearsAfterTreatment = year - currentAge;
    const escalationMultiplier = Math.pow(1 + escalationRate, yearsAfterTreatment - 1);
    const savedKwh = baseKwhSaved * escalationMultiplier;

    labels.push(`Year ${year}`);
    beforeTreatment.push(0);  // No waste after treatment
    afterTreatment.push(Math.round(savedKwh));
  }

  // Calculate totals
  const totalWasted = beforeTreatment.reduce((sum, val) => sum + val, 0);
  const totalSaved = afterTreatment.reduce((sum, val) => sum + val, 0);

  return {
    labels,
    datasets: {
      beforeTreatment,  // Red bars - wasted energy
      afterTreatment    // Green bars - saved energy
    },
    treatmentYear,
    treatmentYearIndex: currentAge - 1,  // 0-indexed
    totals: {
      totalWastedKwh: totalWasted,
      totalWastedDollars: totalWasted * electricityRate,
      totalSavedKwh: totalSaved,
      totalSavedDollars: totalSaved * electricityRate,
      netLifetimeBenefit: (totalSaved - totalWasted) * electricityRate
    },
    metadata: {
      equipmentType,
      tonnage,
      currentAge,
      operatingHours,
      electricityRate,
      escalationRate,
      projectionYears
    }
  };
}

/**
 * Generate before/after treatment data for a PORTFOLIO of equipment
 * Aggregates all equipment's wasted/saved kWh for accurate portfolio totals
 *
 * @param {Object} params - Parameters for calculation
 * @param {Array} params.equipmentList - Array of valid equipment items from ROI results
 * @param {number} params.electricityRate - $/kWh rate
 * @param {number} params.projectionYears - Years to project savings (default 10)
 * @param {number} params.escalationRate - Annual escalation rate (default 0.03)
 * @returns {Object} Data for before/after chart with portfolio totals
 */
export function generatePortfolioBeforeAfterData({
  equipmentList,
  electricityRate,
  projectionYears = 10,
  escalationRate = DEFAULT_ESCALATION_RATE
}) {
  if (!equipmentList || equipmentList.length === 0) return null;

  // Find the oldest equipment to determine chart span
  const maxAge = Math.max(...equipmentList.map(eq => eq.age));
  const treatmentYear = maxAge;  // "Now" is when oldest equipment is at its current age

  const beforeTreatment = [];  // Historical wasted kWh per year
  const afterTreatment = [];   // Future saved kWh per year
  const labels = [];

  // BEFORE: Sum each equipment's wasted kWh for each year of its life
  for (let year = 1; year <= maxAge; year++) {
    let totalWastedKwh = 0;

    for (const eq of equipmentList) {
      // Only include this equipment if it existed during this year
      if (year <= eq.age) {
        const degradation = corpusDegradation(year);
        const multiplier = EQUIPMENT_MULTIPLIERS[eq.equipment_type] || 1.4;
        const runHoursModifier = getRunHoursModifier(eq.operating_hours);
        const wastedKwh = eq.tonnage * (eq.units || 1) * multiplier * eq.operating_hours * degradation * runHoursModifier;
        totalWastedKwh += wastedKwh;
      }
    }

    labels.push(`Year ${year}`);
    beforeTreatment.push(Math.round(totalWastedKwh));
    afterTreatment.push(0);
  }

  // AFTER: Sum all equipment's projected savings
  for (let year = maxAge + 1; year <= maxAge + projectionYears; year++) {
    let totalSavedKwh = 0;
    const yearsAfterTreatment = year - maxAge;

    for (const eq of equipmentList) {
      const improvementRate = getImprovementRate(eq.equipment_type, eq.age, eq.operating_hours);
      const multiplier = EQUIPMENT_MULTIPLIERS[eq.equipment_type] || 1.4;
      const baseKwhSaved = eq.tonnage * (eq.units || 1) * multiplier * eq.operating_hours * improvementRate;
      const escalationMultiplier = Math.pow(1 + escalationRate, yearsAfterTreatment - 1);
      totalSavedKwh += baseKwhSaved * escalationMultiplier;
    }

    labels.push(`Year ${year}`);
    beforeTreatment.push(0);
    afterTreatment.push(Math.round(totalSavedKwh));
  }

  // Calculate totals
  const totalWasted = beforeTreatment.reduce((sum, val) => sum + val, 0);
  const totalSaved = afterTreatment.reduce((sum, val) => sum + val, 0);
  const totalTonnage = equipmentList.reduce((sum, eq) => sum + eq.tonnage * (eq.units || 1), 0);
  const avgAge = Math.round(equipmentList.reduce((sum, eq) => sum + eq.age, 0) / equipmentList.length);

  return {
    labels,
    datasets: {
      beforeTreatment,  // Red bars - wasted energy
      afterTreatment    // Green bars - saved energy
    },
    treatmentYear: maxAge,
    treatmentYearIndex: maxAge - 1,  // 0-indexed
    totals: {
      totalWastedKwh: totalWasted,
      totalWastedDollars: totalWasted * electricityRate,
      totalSavedKwh: totalSaved,
      totalSavedDollars: totalSaved * electricityRate,
      netLifetimeBenefit: (totalSaved - totalWasted) * electricityRate
    },
    metadata: {
      equipmentCount: equipmentList.length,
      totalTonnage,
      avgAge,
      currentAge: maxAge,  // For backwards compatibility with chart component
      operatingHours: Math.round(equipmentList.reduce((sum, eq) => sum + eq.operating_hours, 0) / equipmentList.length),
      electricityRate,
      escalationRate,
      projectionYears
    }
  };
}

/**
 * Calculate weighted average electricity rate across equipment portfolio
 * Weights by operating hours (equipment running more hours has more impact on total cost)
 *
 * @param {Array} equipmentList - Array of valid equipment items
 * @param {number} fallback - Default rate if calculation fails (default 0.12)
 * @returns {number} Weighted average electricity rate in $/kWh
 */
export function calculatePortfolioElectricityRate(equipmentList, fallback = 0.12) {
  if (!equipmentList || equipmentList.length === 0) return fallback;

  const totalHours = equipmentList.reduce((sum, eq) => sum + (eq.operating_hours || 0), 0);
  if (totalHours === 0) return equipmentList[0]?.electricity_rate || fallback;

  return equipmentList.reduce((sum, eq) =>
    sum + ((eq.electricity_rate || fallback) * (eq.operating_hours || 0)), 0
  ) / totalHours;
}

// ============================================================================
// INDIVIDUAL EQUIPMENT CALCULATIONS
// ============================================================================

/**
 * Calculate ROI for a single piece of equipment
 *
 * Uses 6-component corpus-validated value model:
 * 1. Direct energy savings (116 PE-certified studies)
 * 2. Avoided future degradation (ASHRAE 1998 Handbook Ch 2.9)
 * 3. Avoided peak demand charges (Industry kW/ton metrics)
 * 4. Avoided extended runtime (50% capacity-to-runtime factor)
 * 5. Life extension value (20% per roi_calculator_logic.json)
 * 6. Mission-Critical Protection (MTBF improvement × facility downtime cost)
 *
 * @param {Object} equipment - Equipment object from parsed EAS
 * @returns {Object} ROI metrics for this equipment
 */
export function calculateEquipmentROI(equipment) {
  const {
    row_number,
    equipment_id,
    equipment_type,
    tonnage,
    age,
    operating_hours,
    electricity_rate,
    units = 1,
    location,
    demand_rate = 12, // $/kW/month, default commercial rate
    refrigerant_type = 'default',
    replacement_cycle_years = 10, // Company's replacement cycle (default 10 years)
    facility_type = 'commercial', // Facility type for mission-critical protection
    custom_downtime_cost_per_hour = null // Custom $/hour downtime cost (overrides facility_type)
  } = equipment;

  // Validate required fields
  if (!equipment_type || !tonnage || !age || !operating_hours || !electricity_rate) {
    return {
      row_number,
      equipment_id,
      error: 'Missing required fields',
      valid: false
    };
  }

  // Get improvement rate using corpus model (now includes run hours impact)
  const improvementRate = getImprovementRate(equipment_type, age, operating_hours);

  // Get equipment multiplier (kW per ton)
  const multiplier = EQUIPMENT_MULTIPLIERS[equipment_type] || 1.4;

  // Calculate wasted energy before treatment (historical loss)
  const wastedEnergy = calculateWastedEnergy(
    equipment_type,
    tonnage * units,
    age,
    operating_hours,
    electricity_rate
  );

  // ============================================================================
  // 1. DIRECT ENERGY SAVINGS (existing calculation)
  // ============================================================================
  const kwhSavedPerUnit = tonnage * multiplier * operating_hours * improvementRate;
  const totalKwhSaved = kwhSavedPerUnit * units;
  const annualEnergySavings = totalKwhSaved * electricity_rate;

  // Calculate treatment cost
  const treatmentCost = tonnage * TREATMENT_COST_PER_TON * units;

  // ============================================================================
  // 2-5. AVOIDED COST CALCULATIONS (5-component corpus-validated model)
  // ============================================================================

  // 2. Avoided future degradation (ASHRAE 1998 Handbook Ch 2.9: 2-3%/year)
  const avoidedDegradation = calculateAvoidedDegradationCosts({
    equipmentType: equipment_type,
    currentAge: age,
    tonnage: tonnage * units,
    operatingHours: operating_hours,
    electricityRate: electricity_rate,
    years: 10
  });

  // 3. Avoided peak demand charges (Industry kW/ton metrics)
  const avoidedDemand = calculateAvoidedDemandCharges({
    equipmentType: equipment_type,
    currentAge: age,
    tonnage: tonnage * units,
    demandRate: demand_rate,
    years: 10
  });

  // 4. Avoided extended runtime (50% capacity-to-runtime factor)
  // Degraded equipment runs LONGER to meet load - separate from efficiency loss
  const avoidedRuntime = calculateAvoidedExtendedRuntimeCosts({
    equipmentType: equipment_type,
    currentAge: age,
    tonnage: tonnage * units,
    operatingHours: operating_hours,
    electricityRate: electricity_rate,
    years: 10
  });

  // 5. Life extension value (Enhanced with cascading degradation effect)
  // The improvementRate IS the degradation that exists - treatment restores it
  const lifeExtension = calculateLifeExtensionValue({
    equipmentType: equipment_type,
    currentAge: age,
    tonnage: tonnage * units,
    degradationPct: improvementRate, // Improvement rate = existing degradation being restored
    replacementCycleYears: replacement_cycle_years,
    discountRate: 0.08
  });

  // 6. Mission-Critical Protection (MTBF improvement × facility downtime cost)
  // This captures the operational value of improved reliability for mission-critical facilities
  const missionCriticalProtection = calculateMissionCriticalProtection({
    equipmentType: equipment_type,
    currentAge: age,
    facilityType: facility_type,
    customDowntimeCostPerHour: custom_downtime_cost_per_hour,
    operatingHours: operating_hours,
    years: 10
  });

  // ============================================================================
  // TOTAL ANNUAL VALUE (6 corpus-validated components)
  // ============================================================================
  const totalAnnualValue =
    annualEnergySavings +
    avoidedDegradation.annualizedAvoidedCost +
    avoidedDemand.annualizedAvoidedDemand +
    avoidedRuntime.annualizedAvoidedRuntime +
    lifeExtension.annualizedLifeExtensionValue +
    missionCriticalProtection.annualizedValue;

  // Total avoided costs (everything except direct energy savings)
  const totalAnnualAvoidedCosts =
    avoidedDegradation.annualizedAvoidedCost +
    avoidedDemand.annualizedAvoidedDemand +
    avoidedRuntime.annualizedAvoidedRuntime +
    lifeExtension.annualizedLifeExtensionValue +
    missionCriticalProtection.annualizedValue;

  // ============================================================================
  // PAYBACK CALCULATIONS (using total value, not just energy savings)
  // ============================================================================

  // Energy-only payback (legacy, for comparison)
  const energyOnlyPaybackMonths = annualEnergySavings > 0
    ? (treatmentCost / annualEnergySavings) * 12
    : Infinity;

  // TRUE payback using total value
  const truePaybackMonths = totalAnnualValue > 0
    ? (treatmentCost / totalAnnualValue) * 12
    : Infinity;

  // Calculate 5-year ROI using total value
  const fiveYearTotalValue = totalAnnualValue * 5;
  const fiveYearROI = treatmentCost > 0
    ? ((fiveYearTotalValue - treatmentCost) / treatmentCost) * 100
    : 0;

  // Calculate 10-year NPV using total value with proper discounting
  // Using 8% discount rate (typical WACC for commercial projects)
  const discountRate = 0.08;
  let tenYearNPV = -treatmentCost;
  for (let year = 1; year <= 10; year++) {
    tenYearNPV += totalAnnualValue / Math.pow(1 + discountRate, year);
  }

  // Environmental impact
  const co2AvoidedKg = totalKwhSaved * ENVIRONMENTAL_FACTORS.KWH_TO_CO2_KG;
  const co2AvoidedTonnes = co2AvoidedKg / 1000;
  const vehicleEquivalent = co2AvoidedTonnes / ENVIRONMENTAL_FACTORS.CO2_TONNES_PER_CAR_YEAR;

  // Value breakdown percentages
  const energySavingsPct = totalAnnualValue > 0
    ? (annualEnergySavings / totalAnnualValue) * 100
    : 0;
  const avoidedCostsPct = totalAnnualValue > 0
    ? (totalAnnualAvoidedCosts / totalAnnualValue) * 100
    : 0;

  return {
    // Identity
    row_number,
    equipment_id,
    equipment_type,
    location,
    tonnage,
    units,
    age,

    // Operational metrics
    operating_hours,
    electricity_rate,
    improvement_rate: improvementRate,

    // Core financial metrics
    treatment_cost: treatmentCost,

    // Direct energy savings (component 1 - 116 PE-certified studies)
    annual_energy_savings: annualEnergySavings,
    annual_savings: annualEnergySavings, // Legacy alias

    // Avoided cost components (components 2-6, corpus-validated)
    avoided_degradation_annual: avoidedDegradation.annualizedAvoidedCost,
    avoided_demand_annual: avoidedDemand.annualizedAvoidedDemand,
    avoided_runtime_annual: avoidedRuntime.annualizedAvoidedRuntime,
    life_extension_annual: lifeExtension.annualizedLifeExtensionValue,
    mission_critical_protection_annual: missionCriticalProtection.annualizedValue,

    // Total value (6 corpus-validated components)
    total_annual_value: totalAnnualValue,
    total_avoided_costs_annual: totalAnnualAvoidedCosts,

    // Payback metrics
    payback_months: truePaybackMonths, // NEW: Uses total value
    energy_only_payback_months: energyOnlyPaybackMonths, // Legacy comparison

    // ROI metrics
    five_year_roi: fiveYearROI,
    ten_year_npv: tenYearNPV,

    // Energy metrics
    kwh_saved_annual: totalKwhSaved,

    // Environmental metrics
    co2_avoided_tonnes: co2AvoidedTonnes,
    vehicle_equivalent: vehicleEquivalent,

    // Value breakdown (6 corpus-validated components)
    value_breakdown: {
      energy_savings_pct: energySavingsPct,
      avoided_costs_pct: avoidedCostsPct,
      components: {
        energy_savings: annualEnergySavings,
        avoided_degradation: avoidedDegradation.annualizedAvoidedCost,
        avoided_demand: avoidedDemand.annualizedAvoidedDemand,
        avoided_runtime: avoidedRuntime.annualizedAvoidedRuntime,
        life_extension: lifeExtension.annualizedLifeExtensionValue,
        mission_critical_protection: missionCriticalProtection.annualizedValue
      }
    },

    // Detailed breakdowns for advanced analysis
    detailed_breakdown: {
      avoided_degradation: avoidedDegradation,
      avoided_demand: avoidedDemand,
      avoided_runtime: avoidedRuntime,
      life_extension: lifeExtension,
      mission_critical_protection: missionCriticalProtection
    },

    // Wasted energy before treatment (historical loss)
    wasted_energy: wastedEnergy,

    // Status
    valid: true
  };
}

// ============================================================================
// PORTFOLIO-LEVEL CALCULATIONS
// ============================================================================

/**
 * Group equipment by type
 *
 * @param {Array} equipment - Array of equipment objects
 * @returns {Object} Equipment grouped by type
 */
function groupByEquipmentType(equipment) {
  const groups = {};

  equipment.forEach(eq => {
    const type = eq.equipment_type || 'Unknown';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(eq);
  });

  return groups;
}

/**
 * Calculate aggregate metrics for an array of equipment
 *
 * Uses 6-component corpus-validated value model.
 *
 * @param {Array} equipmentArray - Array of equipment ROI objects
 * @returns {Object} Aggregated metrics
 */
function aggregateMetrics(equipmentArray) {
  const validEquipment = equipmentArray.filter(eq => eq.valid);

  if (validEquipment.length === 0) {
    return {
      count: 0,
      total_tonnage: 0,
      avg_electricity_rate: 0.10,
      avg_operating_hours: 0,
      total_treatment_cost: 0,
      total_annual_savings: 0,
      total_annual_value: 0,
      total_avoided_costs: 0,
      average_payback_months: 0,
      energy_only_payback_months: 0,
      average_roi: 0,
      total_kwh_saved: 0,
      total_co2_avoided: 0,
      total_vehicle_equivalent: 0,
      value_breakdown: {
        energy_savings: 0,
        avoided_degradation: 0,
        avoided_demand: 0,
        avoided_runtime: 0,
        life_extension: 0,
        mission_critical_protection: 0
      }
    };
  }

  // Core sums
  const totalTreatmentCost = validEquipment.reduce((sum, eq) => sum + eq.treatment_cost, 0);
  const totalTonnage = validEquipment.reduce((sum, eq) => sum + (eq.tonnage * eq.units), 0);
  const totalUnits = validEquipment.reduce((sum, eq) => sum + (eq.units || 1), 0);

  // Weighted-average electricity rate (weighted by operating hours)
  const totalHoursWeighted = validEquipment.reduce((sum, eq) => sum + (eq.operating_hours || 0), 0);
  const avgElectricityRate = totalHoursWeighted > 0
    ? validEquipment.reduce((sum, eq) => sum + ((eq.electricity_rate || 0.10) * (eq.operating_hours || 0)), 0) / totalHoursWeighted
    : (validEquipment[0]?.electricity_rate || 0.10);
  const avgOperatingHours = validEquipment.length > 0
    ? Math.round(totalHoursWeighted / validEquipment.length)
    : 0;
  const totalKwhSaved = validEquipment.reduce((sum, eq) => sum + eq.kwh_saved_annual, 0);
  const totalCO2Avoided = validEquipment.reduce((sum, eq) => sum + eq.co2_avoided_tonnes, 0);

  // Direct energy savings (component 1 - 116 PE-certified studies)
  const totalEnergySavings = validEquipment.reduce((sum, eq) => sum + eq.annual_energy_savings, 0);

  // Avoided cost components (components 2-6, corpus-validated)
  const totalAvoidedDegradation = validEquipment.reduce((sum, eq) => sum + (eq.avoided_degradation_annual || 0), 0);
  const totalAvoidedDemand = validEquipment.reduce((sum, eq) => sum + (eq.avoided_demand_annual || 0), 0);
  const totalAvoidedRuntime = validEquipment.reduce((sum, eq) => sum + (eq.avoided_runtime_annual || 0), 0);
  const totalLifeExtension = validEquipment.reduce((sum, eq) => sum + (eq.life_extension_annual || 0), 0);
  const totalMissionCriticalProtection = validEquipment.reduce((sum, eq) => sum + (eq.mission_critical_protection_annual || 0), 0);

  // Total annual value (6 corpus-validated components)
  // Note: Falls back to energy-only if total_annual_value is missing
  const totalAnnualValue = validEquipment.reduce((sum, eq) => {
    if (!eq.total_annual_value && eq.annual_savings) {
      console.warn(`Equipment ${eq.equipment_id || 'unknown'} missing total_annual_value, using energy_savings only (understates true value)`);
    }
    return sum + (eq.total_annual_value || eq.annual_savings || 0);
  }, 0);

  // Total avoided costs (everything except direct energy savings)
  const totalAvoidedCosts = totalAnnualValue - totalEnergySavings;

  // Payback calculations
  // TRUE payback using total value
  const truePaybackMonths = totalAnnualValue > 0 ? (totalTreatmentCost / totalAnnualValue) * 12 : 0;

  // Energy-only payback (legacy comparison)
  const energyOnlyPaybackMonths = totalEnergySavings > 0 ? (totalTreatmentCost / totalEnergySavings) * 12 : 0;

  // ROI using total value
  const fiveYearTotalValue = totalAnnualValue * 5;
  const averageROI = totalTreatmentCost > 0 ? ((fiveYearTotalValue - totalTreatmentCost) / totalTreatmentCost) * 100 : 0;

  // Value breakdown percentages
  const energySavingsPct = totalAnnualValue > 0 ? (totalEnergySavings / totalAnnualValue) * 100 : 0;
  const avoidedCostsPct = totalAnnualValue > 0 ? (totalAvoidedCosts / totalAnnualValue) * 100 : 0;

  return {
    count: totalUnits,
    total_tonnage: totalTonnage,
    avg_electricity_rate: avgElectricityRate,
    avg_operating_hours: avgOperatingHours,
    total_treatment_cost: totalTreatmentCost,

    // Direct energy savings
    total_annual_savings: totalEnergySavings,

    // Comprehensive value
    total_annual_value: totalAnnualValue,
    total_avoided_costs: totalAvoidedCosts,

    // Payback (using total value)
    average_payback_months: truePaybackMonths,
    energy_only_payback_months: energyOnlyPaybackMonths,

    // ROI
    average_roi: averageROI,

    // Energy/environmental
    total_kwh_saved: totalKwhSaved,
    total_co2_avoided: totalCO2Avoided,
    total_vehicle_equivalent: totalCO2Avoided / ENVIRONMENTAL_FACTORS.CO2_TONNES_PER_CAR_YEAR,

    // Value breakdown (6 corpus-validated components)
    value_breakdown: {
      energy_savings: totalEnergySavings,
      avoided_degradation: totalAvoidedDegradation,
      avoided_demand: totalAvoidedDemand,
      avoided_runtime: totalAvoidedRuntime,
      life_extension: totalLifeExtension,
      mission_critical_protection: totalMissionCriticalProtection
    },

    // Value composition percentages
    value_composition: {
      energy_savings_pct: energySavingsPct,
      avoided_costs_pct: avoidedCostsPct
    }
  };
}

/**
 * Calculate portfolio-level metrics
 *
 * Uses 6-component corpus-validated value model.
 *
 * @param {Array} individualResults - Array of individual equipment ROI results
 * @returns {Object} Portfolio-wide metrics
 */
function calculatePortfolioMetrics(individualResults) {
  const aggregate = aggregateMetrics(individualResults);

  return {
    summary: {
      total_equipment: aggregate.count,
      total_tonnage: aggregate.total_tonnage,
      total_treatment_cost: aggregate.total_treatment_cost,

      // Direct energy savings (legacy, for backward compatibility)
      total_annual_savings: aggregate.total_annual_savings,

      // COMPREHENSIVE VALUE (5 corpus-validated components)
      total_annual_value: aggregate.total_annual_value,
      total_avoided_costs: aggregate.total_avoided_costs,

      // Payback using total value (not just energy savings)
      weighted_average_payback_months: aggregate.average_payback_months,
      energy_only_payback_months: aggregate.energy_only_payback_months,

      five_year_roi: aggregate.average_roi,
      total_kwh_saved: aggregate.total_kwh_saved,
      avg_electricity_rate: aggregate.avg_electricity_rate,
      avg_operating_hours: aggregate.avg_operating_hours
    },
    environmental: {
      co2_avoided_tonnes: aggregate.total_co2_avoided,
      vehicle_equivalent: Math.round(aggregate.total_vehicle_equivalent)
    },
    projections: {
      // Using total value for projections
      year_1_value: aggregate.total_annual_value,
      year_5_cumulative_value: aggregate.total_annual_value * 5,
      year_10_cumulative_value: aggregate.total_annual_value * 10,
      year_5_net: (aggregate.total_annual_value * 5) - aggregate.total_treatment_cost,
      year_10_net: (aggregate.total_annual_value * 10) - aggregate.total_treatment_cost,

      // Legacy: energy-only projections for comparison
      year_1_savings: aggregate.total_annual_savings,
      year_5_cumulative: aggregate.total_annual_savings * 5,
      year_10_cumulative: aggregate.total_annual_savings * 10
    },
    value_breakdown: aggregate.value_breakdown,
    value_composition: aggregate.value_composition
  };
}

/**
 * Calculate metrics by equipment type
 *
 * Uses 6-component corpus-validated value model for each equipment type.
 *
 * @param {Object} groupedEquipment - Equipment grouped by type
 * @param {Array} individualResults - Individual ROI results
 * @returns {Object} Metrics by equipment type
 */
function calculateByTypeMetrics(groupedEquipment, individualResults) {
  const byType = {};

  Object.keys(groupedEquipment).forEach(type => {
    // Get individual results for this type
    const typeResults = individualResults.filter(result => result.equipment_type === type && result.valid);

    // Aggregate metrics
    const aggregate = aggregateMetrics(typeResults);

    byType[type] = {
      count: aggregate.count,
      total_tonnage: aggregate.total_tonnage,
      total_treatment_cost: aggregate.total_treatment_cost,

      // Direct energy savings
      total_annual_savings: aggregate.total_annual_savings,

      // Comprehensive value (5 corpus-validated components)
      total_annual_value: aggregate.total_annual_value,
      total_avoided_costs: aggregate.total_avoided_costs,

      // Payback (using total value)
      average_payback_months: aggregate.average_payback_months,
      energy_only_payback_months: aggregate.energy_only_payback_months,

      five_year_roi: aggregate.average_roi,
      total_kwh_saved: aggregate.total_kwh_saved,
      co2_avoided_tonnes: aggregate.total_co2_avoided,

      // Value breakdown for this equipment type
      value_breakdown: aggregate.value_breakdown,
      value_composition: aggregate.value_composition
    };
  });

  return byType;
}

// ============================================================================
// MAIN ROI CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate comprehensive ROI for entire EAS dataset
 *
 * @param {Object} easData - Parsed EAS data from easParser.js
 * @param {Object} companyConfig - Optional company configuration
 * @param {number} companyConfig.blendedElectricityRate - All-in $/kWh (default 0.10)
 * @param {number} companyConfig.equipmentReplacementCycle - Years (default 10)
 * @param {string} companyConfig.facilityType - Facility type for mission-critical protection
 * @param {number} companyConfig.customDowntimeCost - Custom $/hour downtime cost
 * @returns {Object} Complete ROI analysis at all levels
 */
export function calculateEASROI(easData, companyConfig = {}) {
  const { equipment } = easData;
  const defaultRate = companyConfig.blendedElectricityRate || 0.10;
  const replacementCycle = companyConfig.equipmentReplacementCycle || 10;
  const facilityType = companyConfig.facilityType || 'commercial';
  const customDowntimeCost = companyConfig.customDowntimeCost || null;

  // Calculate individual equipment ROI, passing default rate, replacement cycle, and facility type
  const individualResults = equipment.map(eq => calculateEquipmentROI({
    ...eq,
    electricity_rate: eq.electricity_rate || defaultRate,
    replacement_cycle_years: replacementCycle,
    facility_type: facilityType,
    custom_downtime_cost_per_hour: customDowntimeCost
  }));

  // Group equipment by type
  const groupedEquipment = groupByEquipmentType(equipment);

  // Calculate portfolio-level metrics
  const portfolioMetrics = calculatePortfolioMetrics(individualResults);

  // Calculate by-type metrics
  const byTypeMetrics = calculateByTypeMetrics(groupedEquipment, individualResults);

  // Calculate validation summary
  const validCount = individualResults.filter(r => r.valid).length;
  const invalidCount = individualResults.filter(r => !r.valid).length;

  return {
    portfolio: portfolioMetrics,
    byType: byTypeMetrics,
    individual: individualResults,
    validation: {
      total: equipment.length,
      valid: validCount,
      invalid: invalidCount,
      completeness: Math.round((validCount / equipment.length) * 100)
    },
    metadata: {
      calculated_at: new Date().toISOString(),
      model_version: 'corpus-v3.1-validated',
      treatment_cost_per_ton: TREATMENT_COST_PER_TON,
      value_model: {
        description: '6-component corpus-validated value model',
        components: [
          'Direct energy savings (116 PE-certified studies)',
          'Avoided future degradation (ASHRAE 1998 Handbook Ch 2.9)',
          'Avoided peak demand charges (Industry kW/ton metrics)',
          'Avoided extended runtime (50% capacity-to-runtime factor)',
          'Life extension value (20% per roi_calculator_logic.json)',
          'Mission-Critical Protection (MTBF improvement × facility downtime cost)'
        ],
        removed_components: [
          'Avoided maintenance (30%) - qualitative only',
          'Avoided refrigerant (40%) - "may reduce" language',
          'Avoided compressor (50%) - lab wear data only'
        ],
        projection_years: 10,
        assumptions: {
          energy_savings: '23.2% mean improvement (116 PE-certified studies)',
          annual_degradation_rate: '2-3%/year (ASHRAE 1998 Handbook Ch 2.9)',
          demand_factor: '0.8× degradation to kW correlation',
          runtime_extension: '50% of degradation above 15% threshold',
          life_extension: '20% longer equipment life (roi_calculator_logic.json)',
          mission_critical_mtbf: '35% MTBF improvement reduces failures'
        },
        corpus_sources: {
          energy_savings: 'manifest.json → stats.pe_certified_studies',
          degradation: 'ashrae_references.json → cumulative_calculation',
          life_extension: 'roi_calculator_logic.json line 113',
          mission_critical: 'MTBF tables from corpus failure analysis'
        }
      }
    }
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format currency value
 *
 * @param {number} value - Dollar amount
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Format number with commas
 *
 * @param {number} value - Number to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted number string
 */
export function formatNumber(value, decimals = 0) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Main functions
  calculateEASROI,
  calculateEquipmentROI,

  // New improvement features from 2017 IPP Calculator
  calculateWastedEnergy,
  calculateProjectionsWithEscalation,
  generateBeforeAfterData,
  generatePortfolioBeforeAfterData,
  calculatePortfolioElectricityRate,

  // Utilities
  formatCurrency,
  formatNumber,

  // Constants
  TREATMENT_COST_PER_TON,
  EQUIPMENT_MULTIPLIERS,
  ENVIRONMENTAL_FACTORS,
  DEFAULT_ESCALATION_RATE
};
